import React, { useCallback, useEffect, useMemo, useState } from "react";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { getDocumentTranslator, documentStatus } from "./documentPopupMessages";
import "../../PurchasingUi.css";
import "./PurchasingDocumentPopups.css";
import {
  FiCheckCircle,
  FiClock,
  FiEdit3,
  FiFileText,
  FiPackage,
  FiRefreshCw,
  FiRepeat,
  FiSend,
  FiShield,
  FiTruck,
  FiUserCheck,
  FiX,
} from "react-icons/fi";
import axios from "../../../api/axiosInstance";
import useCrossTabDataRefreshToken from "../../../hooks/useCrossTabDataRefresh";

const iconByStep = {
  request: FiFileText,
  convert: FiRepeat,
  approve: FiShield,
  send: FiSend,
  acknowledge: FiUserCheck,
  receive: FiTruck,
  delivery_note: FiEdit3,
  verify: FiCheckCircle,
  inventory: FiPackage,
};

const statusText = {
  completed: "Completed",
  current: "Current step",
  partial: "Partial",
  upcoming: "Next",
  skipped: "Not recorded",
  blocked: "Blocked",
};

const trackingCss = `
  .rqt-overlay,.rqt-modal,.rqt-body,.rqt-group,.rqt-flow,.rqt-step,.rqt-product{box-sizing:border-box}
  .rqt-overlay{position:fixed;inset:0;z-index:10030;display:flex;align-items:center;justify-content:center;padding:10px;background:var(--pu-overlay);backdrop-filter:blur(4px)}
  .rqt-modal{width:min(1680px,calc(100vw - 20px));max-height:calc(100vh - 20px);display:flex;flex-direction:column;overflow:hidden;border:1px solid var(--pu-border);border-radius:26px;background:var(--pu-surface);box-shadow:var(--pu-shadow);color:var(--pu-text)}
  .rqt-head{position:relative;display:flex;align-items:flex-start;justify-content:space-between;gap:20px;padding:22px 24px 18px;border-bottom:1px solid var(--pu-border);background:var(--pu-soft)}
  .rqt-kicker{display:flex;align-items:center;gap:8px;margin-bottom:7px;color:var(--pu-text);font-size:.72rem;font-weight:900;letter-spacing:1.35px;text-transform:uppercase}
  .rqt-title{margin:0;font-size:clamp(1.35rem,2.1vw,2rem);line-height:1.05;font-weight:950;letter-spacing:-.025em;color:var(--pu-text)}
  .rqt-subtitle{display:flex;align-items:center;gap:9px;flex-wrap:wrap;margin-top:9px;color:var(--pu-muted);font-size:.84rem}
  .rqt-headStatus{display:inline-flex;align-items:center;padding:4px 9px;border-radius:999px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-text);font-size:.68rem;font-weight:850;letter-spacing:.45px;text-transform:uppercase}
  .rqt-headActions{display:flex;align-items:center;gap:8px;flex:0 0 auto}
  .rqt-iconBtn{width:39px;height:39px;display:inline-flex;align-items:center;justify-content:center;border-radius:12px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-text);cursor:pointer;transition:transform .15s ease,border-color .15s ease,background .15s ease}
  .rqt-iconBtn:hover{transform:none;border-color:var(--pu-border);background:var(--pu-soft)}
  .rqt-iconBtn:disabled{opacity:1;cursor:wait;transform:none}
  .rqt-body{min-height:300px;overflow:auto;padding:18px 20px 24px;scrollbar-width:thin;scrollbar-color:var(--pu-border) var(--pu-soft)}
  .rqt-summary{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px;margin-bottom:16px}
  .rqt-summaryCard{padding:11px 13px;border:1px solid var(--pu-border);border-radius:14px;background:var(--pu-soft)}
  .rqt-summaryLabel{margin-bottom:4px;color:var(--pu-muted);font-size:.66rem;font-weight:800;letter-spacing:.65px;text-transform:uppercase}
  .rqt-summaryValue{overflow:hidden;color:var(--pu-text);font-size:.94rem;font-weight:850;text-overflow:ellipsis;white-space:nowrap}
  .rqt-groups{display:flex;flex-direction:column;gap:16px}
  .rqt-group{position:relative;overflow:hidden;padding:17px;border:1px solid var(--pu-border);border-radius:20px;background:var(--pu-soft);box-shadow:none}
  .rqt-group::before{content:"";position:absolute;inset:0 auto 0 0;width:3px;background:var(--pu-soft)}
  .rqt-groupHead{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;margin-bottom:15px}
  .rqt-groupKicker{margin-bottom:4px;color:var(--pu-text);font-size:.67rem;font-weight:850;letter-spacing:.75px;text-transform:uppercase}
  .rqt-groupTitle{margin:0;color:var(--pu-text);font-size:1.04rem;font-weight:900}
  .rqt-groupMeta{display:flex;align-items:center;gap:8px;margin-top:6px;color:var(--pu-muted);font-size:.76rem}
  .rqt-currentBadge{display:inline-flex;align-items:center;gap:7px;max-width:260px;padding:7px 10px;border:1px solid var(--pu-border);border-radius:999px;background:var(--pu-soft);color:var(--pu-text);font-size:.73rem;font-weight:850;text-align:right}
  .rqt-currentBadge--blocked{border-color:var(--pu-danger);background:var(--pu-danger-bg);color:var(--pu-danger)}
  .rqt-currentBadge--partial{border-color:var(--pu-border);background:var(--pu-soft);color:var(--pu-text)}
  .rqt-progressTrack{height:4px;overflow:hidden;margin:0 0 16px;border-radius:99px;background:var(--pu-soft)}
  .rqt-progressBar{height:100%;border-radius:inherit;background:#223a7a;box-shadow:none;transition:width .25s ease}
  .rqt-flowShell{overflow-x:auto;padding:3px 1px 10px;scrollbar-width:thin;scrollbar-color:var(--pu-border) var(--pu-soft)}
  .rqt-flow{display:flex;align-items:stretch;min-width:max-content}
  .rqt-step{--accent:var(--pu-primary);position:relative;width:146px;min-height:142px;display:flex;flex-direction:column;padding:11px;border:1px solid color-mix(in srgb,var(--accent) 46%,var(--pu-border));border-radius:15px;background:var(--pu-soft);box-shadow:none}
  .rqt-step--request{--accent:var(--pu-primary)}.rqt-step--convert{--accent:var(--pu-primary)}.rqt-step--approve{--accent:var(--pu-primary)}.rqt-step--send{--accent:var(--pu-primary)}.rqt-step--acknowledge{--accent:var(--pu-primary)}.rqt-step--receive{--accent:var(--pu-primary)}.rqt-step--delivery_note{--accent:var(--pu-primary)}.rqt-step--verify{--accent:var(--pu-primary)}.rqt-step--inventory{--accent:var(--pu-primary)}
  .rqt-step--completed{border-color:var(--pu-border);background:var(--pu-soft);filter:none;opacity:1}
  .rqt-step--current,.rqt-step--partial{transform:none;border-color:var(--pu-border);box-shadow:none}
  .rqt-step--blocked{--accent:var(--pu-primary);border-color:var(--pu-danger);background:var(--pu-danger-bg)}
  .rqt-step--skipped{--accent:var(--pu-primary);border-style:dashed;opacity:1;filter:none}
  .rqt-stepIcon{width:31px;height:31px;display:flex;align-items:center;justify-content:center;margin-bottom:9px;border:1px solid color-mix(in srgb,var(--accent) 44%,var(--pu-border));border-radius:10px;background:var(--pu-soft);color:var(--pu-text);font-size:1rem}
  .rqt-stepLabel{color:var(--pu-muted);font-size:.79rem;font-weight:900;line-height:1.15}
  .rqt-stepState{margin-top:4px;color:var(--pu-muted);font-size:.62rem;font-weight:850;letter-spacing:.45px;text-transform:uppercase}
  .rqt-stepBottom{margin-top:auto;padding-top:9px;color:var(--pu-muted);font-size:.64rem;line-height:1.3}
  .rqt-stepDate{display:flex;align-items:flex-start;gap:5px;color:var(--pu-muted)}
  .rqt-stepDetail{margin-top:4px;display:-webkit-box;overflow:hidden;-webkit-box-orient:vertical;-webkit-line-clamp:2}
  .rqt-arrow{width:32px;display:flex;align-items:center;justify-content:center;flex:0 0 32px;color:var(--pu-text)}
  .rqt-arrow::before{content:"";width:19px;height:2px;border-radius:99px;background:var(--pu-soft)}
  .rqt-arrow::after{content:"";width:7px;height:7px;margin-left:-6px;border-top:2px solid var(--pu-border);border-right:2px solid var(--pu-border);transform:rotate(45deg)}
  .rqt-productsTitle{display:flex;align-items:center;gap:8px;margin:5px 0 9px;color:var(--pu-text);font-size:.7rem;font-weight:900;letter-spacing:.65px;text-transform:uppercase}
  .rqt-products{display:grid;grid-template-columns:repeat(auto-fit,minmax(250px,1fr));gap:9px}
  .rqt-product{min-width:0;padding:11px 12px;border:1px solid var(--pu-border);border-radius:14px;background:var(--pu-soft)}
  .rqt-productTop{display:flex;align-items:flex-start;justify-content:space-between;gap:10px}
  .rqt-productCode{margin-bottom:3px;color:var(--pu-text);font-size:.65rem;font-weight:900;letter-spacing:.5px}
  .rqt-productName{color:var(--pu-text);font-size:.82rem;font-weight:850;line-height:1.25}
  .rqt-supplier{max-width:46%;overflow:hidden;padding:4px 7px;border:1px solid var(--pu-border);border-radius:8px;background:var(--pu-soft);color:var(--pu-text);font-size:.62rem;font-weight:800;text-overflow:ellipsis;white-space:nowrap}
  .rqt-productMeta{display:flex;align-items:center;gap:7px;flex-wrap:wrap;margin-top:9px}
  .rqt-metaPill{padding:3px 6px;border-radius:7px;background:var(--pu-soft);color:var(--pu-text);font-size:.62rem;font-weight:750}
  .rqt-metaPill strong{color:var(--pu-text);font-weight:900}
  .rqt-orderCode{color:var(--pu-text)}
  .rqt-state{min-height:270px;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:12px;color:var(--pu-text);text-align:center}
  .rqt-spinner{width:34px;height:34px;border:3px solid var(--pu-border);border-top-color:#22d3ee;border-radius:50%;animation:rqt-spin .8s linear infinite}
  .rqt-error{color:var(--pu-danger)}.rqt-errorIcon{font-size:1.6rem;color:var(--pu-danger)}
  @keyframes rqt-spin{to{transform:none}}
  @media(max-width:800px){.rqt-overlay{padding:0}.rqt-modal{width:100vw;max-height:100vh;height:100vh;border-radius:0}.rqt-head{padding:18px 15px 14px}.rqt-title{font-size:1.3rem}.rqt-subtitle{font-size:.75rem}.rqt-body{padding:13px 11px 22px}.rqt-summary{grid-template-columns:repeat(2,minmax(0,1fr))}.rqt-group{padding:14px 11px}.rqt-groupHead{flex-direction:column;gap:8px}.rqt-currentBadge{max-width:100%;text-align:left}.rqt-flowShell{overflow:visible}.rqt-flow{min-width:0;flex-direction:column}.rqt-step{width:100%;min-height:108px}.rqt-arrow{width:100%;height:28px;flex-basis:28px;transform:rotate(90deg)}.rqt-products{grid-template-columns:1fr}}
`;

const formatDateTime = (value, locale) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (value, locale) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function StepTimestamp({ step, locale }) {
  if (!step.completed_at) return null;
  if (step.has_multiple_dates && step.completed_from !== step.completed_at) {
    return (
      <div className="rqt-stepDate">
        <FiClock />
        <span>
          {formatDateTime(step.completed_from, locale)}
          <br />
          {formatDateTime(step.completed_at, locale)}
        </span>
      </div>
    );
  }
  return (
    <div className="rqt-stepDate">
      <FiClock />
      <span>{formatDateTime(step.completed_at, locale)}</span>
    </div>
  );
}

function TrackingFlow({ steps, t, locale, shared = false }) {
  return (
    <div className={`rqt-flowShell${shared ? " rqt-flowShell--shared" : ""}`}>
      <div className="rqt-flow">
        {steps.map((step, index) => {
          const StepIcon = iconByStep[step.key] || FiClock;
          return (
            <React.Fragment key={step.key}>
              <div className={`rqt-step rqt-step--${step.key} rqt-step--${step.status}`}>
                <div className="rqt-stepIcon"><StepIcon /></div>
                <div className="rqt-stepLabel">{t(step.label)}</div>
                <div className="rqt-stepState">{t(statusText[step.status] || step.status)}</div>
                <div className="rqt-stepBottom">
                  <StepTimestamp step={step} locale={locale} />
                  {step.detail && <div className="rqt-stepDetail">{t(step.detail)}</div>}
                </div>
              </div>
              {index < steps.length - 1 && <div className="rqt-arrow" aria-hidden="true" />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

const productCountLabel = (count, languageId) => `${count} ${languageId === "es"
  ? (count === 1 ? "artículo" : "artículos")
  : (count === 1 ? "product" : "products")}`;

function TrackingJourney({ group, t, locale, languageId }) {
  const branches = Array.isArray(group.branches) ? group.branches : [];
  const receiveIndex = group.steps.findIndex((step) => step.key === "receive");
  if (!branches.length || receiveIndex < 0) {
    return <TrackingFlow steps={group.steps} t={t} locale={locale} />;
  }

  return (
    <>
      <TrackingFlow steps={group.steps.slice(0, receiveIndex + 1)} t={t} locale={locale} shared />
      <div className="rqt-branches">
        {branches.map((branch) => (
          <section className={`rqt-branch rqt-branch--${branch.key}`} key={branch.key} aria-label={t(branch.label)}>
            <div className="rqt-branchHead">
              <h6 className="rqt-branchTitle">{t(branch.label)}</h6>
              <span className="rqt-branchCount">{productCountLabel(branch.product_count, languageId)}</span>
            </div>
            <ul className="rqt-branchProducts">
              {branch.products.map((product) => (
                <li className="rqt-branchProduct" key={`${product.requisition_line_id}-${product.po_line_id || "pending"}`}>
                  <span className="rqt-branchProductName">{product.name} <span className="rqt-branchProductCode">({product.code || `ITEM-${product.item_id}`})</span></span>
                  <span className="rqt-branchQuantity">{t(branch.key === "received" ? "Received" : "Pending")} <strong>{product.branch_quantity}</strong></span>
                </li>
              ))}
            </ul>
            <TrackingFlow steps={branch.steps} t={t} locale={locale} />
          </section>
        ))}
      </div>
    </>
  );
}

export default function RequisitionTrackingPopup({ isOpen, requisition, onClose }) {
  const { themeId, languageId } = usePmsTheme();
  const t = getDocumentTranslator(languageId);
  const locale = languageId === "es" ? "es-ES" : "en-GB";
  const refreshToken = useCrossTabDataRefreshToken();
  const requisitionId = requisition?.id || requisition?.raw?.id || null;
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadTracking = useCallback(async () => {
    if (!requisitionId) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`/api/purchasing/requisitions/${requisitionId}/tracking`);
      setTracking(res.data || null);
    } catch (err) {
      console.error("[RequisitionTrackingPopup] Error loading tracking:", err);
      setError(err?.response?.data?.error || t("Could not load order tracking."));
    } finally {
      setLoading(false);
    }
  }, [requisitionId, t]);

  useEffect(() => {
    if (!isOpen || !requisitionId) return;
    setTracking(null);
    loadTracking();
  }, [isOpen, requisitionId, loadTracking, refreshToken]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const groups = useMemo(
    () => (Array.isArray(tracking?.groups) ? tracking.groups : []),
    [tracking]
  );

  if (!isOpen) return null;

  return (
    <div className="rqt-overlay pilcore-purchasing-overlay pilcore-document-popup" data-theme={themeId} role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <style>{trackingCss}</style>
      <section className="rqt-modal" role="dialog" aria-modal="true" aria-labelledby="rqt-title">
        <header className="rqt-head">
          <div>
            <div className="rqt-kicker"><FiPackage /> {t("Full order journey")}</div>
            <h4 className="rqt-title" id="rqt-title">{t("Track")} {tracking?.requisition?.code || requisition?.requisitionCode || t("Purchase Requisition")}
            </h4>
            <div className="rqt-subtitle">
              <span className="rqt-headStatus">
                {documentStatus(tracking?.requisition?.status || requisition?.statusLabel || "loading", t)}
              </span>
              <span>{t("Product-level progress from request to inventory")}</span>
            </div>
          </div>
          <div className="rqt-headActions">
            <button type="button" className="rqt-iconBtn" onClick={onClose} aria-label={t("Close tracking")}>
              <FiX />
            </button>
          </div>
        </header>

        <div className="rqt-body">
          {loading && !tracking ? (
            <div className="rqt-state"><div className="rqt-spinner" /><span>{t("Building the product journeys...")}</span></div>
          ) : error ? (
            <div className="rqt-state rqt-error">
              <FiX className="rqt-errorIcon" />
              <span>{error}</span>
              <button type="button" className="rqt-iconBtn" onClick={loadTracking} aria-label={t("Retry")}><FiRefreshCw /></button>
            </div>
          ) : (
            <>
              <div className="rqt-summary">
                <div className="rqt-summaryCard"><div className="rqt-summaryLabel">{t("Request date")}</div><div className="rqt-summaryValue">{formatDate(tracking?.requisition?.request_date, locale)}</div></div>
                <div className="rqt-summaryCard"><div className="rqt-summaryLabel">{t("Expected delivery")}</div><div className="rqt-summaryValue">{formatDate(tracking?.requisition?.expected_delivery_date, locale)}</div></div>
                <div className="rqt-summaryCard"><div className="rqt-summaryLabel">{t("Products tracked")}</div><div className="rqt-summaryValue">{tracking?.product_count || 0}</div></div>
                <div className="rqt-summaryCard"><div className="rqt-summaryLabel">{t("Workflow groups")}</div><div className="rqt-summaryValue">{groups.length}</div></div>
              </div>

              {groups.length ? (
                <div className="rqt-groups">
                  {groups.map((group, groupIndex) => (
                    <article className="rqt-group" key={group.id}>
                      <div className="rqt-groupHead">
                        <div>
                          <div className="rqt-groupKicker">{t("Journey group")} {groupIndex + 1}</div>
                          <h5 className="rqt-groupTitle">{t(group.current_label)}</h5>
                          <div className="rqt-groupMeta">
                            <span>{productCountLabel(group.product_count, languageId)}</span>
                            <span>•</span>
                            <span>{group.progress_percent}{t("% of workflow completed")}</span>
                          </div>
                        </div>
                        <span className={`rqt-currentBadge rqt-currentBadge--${group.current_status}`}>
                          <FiClock /> {t(group.current_label)}
                        </span>
                      </div>

                      <div className="rqt-progressTrack"><div className="rqt-progressBar" style={{ width: `${group.progress_percent}%` }} /></div>

                      <TrackingJourney group={group} t={t} locale={locale} languageId={languageId} />

                      <div className="rqt-productsTitle"><FiPackage /> {t("Products in this journey")}</div>
                      <div className="rqt-products">
                        {group.products.map((product) => (
                          <div className="rqt-product" key={`${group.id}-${product.requisition_line_id}-${product.po_line_id || "pending"}`}>
                            <div className="rqt-productTop">
                              <div>
                                <div className="rqt-productCode">{product.code || `ITEM-${product.item_id}`}</div>
                                <div className="rqt-productName">{product.name}</div>
                              </div>
                              <span className="rqt-supplier" title={product.supplier_name}>{product.supplier_name === "Supplier not assigned" ? t("Supplier not assigned") : product.supplier_name}</span>
                            </div>
                            <div className="rqt-productMeta">
                              {product.order_code && <span className="rqt-metaPill rqt-orderCode">{product.order_code}</span>}
                              <span className="rqt-metaPill">{t("Requested")} <strong>{product.requisition_quantity}</strong></span>
                              {(product.po_line_id || product.order_code) && product.ordered_quantity != null && <span className="rqt-metaPill">{t("Ordered")} <strong>{product.ordered_quantity}</strong></span>}
                              <span className="rqt-metaPill">{t("Received")} <strong>{product.received_quantity}</strong></span>
                              {(product.po_line_id || product.order_code) && product.pending_quantity != null && <span className="rqt-metaPill">{t("Pending")} <strong>{product.pending_quantity}</strong></span>}
                              {Number(product.cancelled_quantity) > 0 && <span className="rqt-metaPill">{t("Cancelled")} <strong>{product.cancelled_quantity}</strong></span>}
                              <span className="rqt-metaPill">{t("Inventory")} <strong>{product.inventory_quantity}</strong></span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="rqt-state"><FiPackage className="rqt-errorIcon" /><span>{t("No product lines are available to track.")}</span></div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
